export interface MenuNode {
  title: string;
  icon?: string;
  access?: string | string[];
  route?: string | string[];
  href?: string;
  newTab?: boolean;
  hint?: string;
  action?: () => void;
  children?: MenuNode[];
}

export interface MenuView extends Omit<MenuNode, 'children'> {
  id: string;
  kind: 'group' | 'route' | 'href' | 'action' | 'inert';
  children?: MenuView[];
}

export interface MenuActions {
  openReport(id: number): void;
  openAddVehicle(): void;
  openExcelUploader(): void;
  openTripModal(): void;
  openHolidayModal(): void;
  openTaxiReservation(isCity: boolean): void;
  openExternalLink(type: number): void;
  openCompanyInfo(): void;
  openModal(selector: string): void;
}

const hasAny = (access: string | string[], can: (key: string) => boolean) =>
  ([] as string[]).concat(access).some(can);

const leafKind = (node: MenuNode): MenuView['kind'] =>
  node.route ? 'route' : node.href ? 'href' : node.action ? 'action' : 'inert';

export function buildMenuView(nodes: MenuNode[], can: (key: string) => boolean, parentId = ''): MenuView[] {
  const result: MenuView[] = [];
  nodes.forEach((node, index) => {
    if (node.access && !hasAny(node.access, can)) return;
    const id = `${parentId}/${index}`;
    const { children: childNodes, ...rest } = node;
    if (!childNodes) {
      result.push({ ...rest, id, kind: leafKind(node) });
      return;
    }
    const children = buildMenuView(childNodes, can, id);
    if (children.length) result.push({ ...rest, id, kind: 'group', children });
  });
  return result;
}

export const createMenu = (a: MenuActions): MenuNode[] => [
    {
      title: 'گزارش و داشبورد',
      icon: 'fa-bar-chart',
      children: [
        {
          title: 'عملکرد',
          children: [
            { title: 'گزارش پیمایش دوره‌ای', icon: '', access: 'bReportAllNavigation', action: () => a.openReport(1) },
            { title: 'خالص کارکرد روزانه', icon: '', access: 'bReportAllFunction', action: () => a.openReport(3) },
          ],
        },
        {
          title: 'سوخت',
          access: 'bReportAllFule',
          children: [
            { title: 'سوخت مصرفی براساس عملکرد', icon: '', access: 'bReportFuleBasePerformance_All', action: () => a.openReport(2) },
            {
              title: 'سوخت خالص مصرفی خودروها',
              icon: '',
              access: 'bReportNetfuelconsumption_All',
              route: '/reportage/fuel-consumption',
              newTab: true,
            },
            { title: 'مصرف بر اساس فلومتر', icon: 'fa-copy', access: 'bReportfuelBaseFlowmeter_All', action: () => a.openReport(7) },
            {
              title: 'سوخت براساس گزارش ثبت شده',
              icon: '',
              access: 'bReportfuelBaseReport_All',
              route: '/reportage/fuel-based-report',
              newTab: true,
            },
          ],
        },
        {
          title: 'محدوده',
          children: [
            { title: 'خودروهای واقع در محدوده', icon: '', access: 'bMobilesArGeoFance', route: ['/report', 'presenceCheck'], newTab: true },
            { title: 'آخرین حضور در محدوده', icon: '', access: 'bMobileInGeoFenceHistory', route: ['/report', 'historyCheck'], newTab: true },
            { title: 'بازرسی تاسیسات', icon: '', access: 'bMobilesInspection', action: () => a.openReport(18) },
            { title: 'خدمات رسانی خودرو‌ها', icon: '', access: 'bServiceDelivery', action: () => a.openReport(19) },
            { title: 'حضور در محدوده', icon: '', access: 'bMobilesFunctionInDomain', href: '/reportArea' },
            { title: 'کارکرد مابین مبدا و مقصد', icon: '', access: 'bMobilesFunctionSD', action: () => a.openReport(6) },
          ],
        },
        {
          title: 'تعمیرات و نگهداری',
          children: [
            { title: 'تعمیرات', icon: '', access: 'bRepairsReport', action: () => a.openReport(5) },
            { title: 'بازدیدهای دوره‌ای', icon: '', access: 'bPeriodicVisits_Report', route: '/periodic-visits', newTab: true },
            { title: 'گزارش بازرسی‌ها', icon: '', access: 'bFInspection_Report', route: '/inspection-search', newTab: true },
            { title: 'سررسید سرویس‌ها', icon: '', access: 'bServiceReport', route: ['/report', 'repairServices'], newTab: true },
          ],
        },
        {
          title: 'مالی',
          access: ['bMobilesSalary', 'bMobilesImportTimesheet', 'bFleetYar_PaymentReport'],
          children: [
            { title: 'حقوق رانندگان', icon: '', access: 'bMobilesSalary', action: () => a.openReport(4) },
            { title: 'ورود ساعت کارکرد خودروها', icon: '', access: 'bMobilesImportTimesheet', action: () => a.openExcelUploader() },
            { title: 'پرداخت‌ها', icon: '', access: 'bFleetYar_PaymentReport', href: '/report-payment' },
            { title: 'خلافی‌ها', icon: '' },
          ],
        },
        {
          title: 'اعتبار',
          access: 'bFleetYar_GPSOFF_List',
          children: [
            { title: 'تمدید اعتبار خودروها', icon: '', route: '/vehicle-renewal', newTab: true },
            { title: 'ردیاب‌های قطع', icon: '', access: 'bFleetYar_GPSOFF_List', route: '/vehicles-offline', newTab: true },
          ],
        },
        {
          title: 'داشبورد',
          access: ['bEnableDashboard', 'bEnableDashboardProfessional', 'bEnableDashboardHybrid', 'bFisheries_Dashboard', 'bFleetYar_Advance_PreregistratioDashboard'],
          children: [
            { title: 'عمومی', icon: '', access: 'bEnableDashboard', href: '/dashbord' },
            { title: 'تخصصی', icon: '', access: 'bEnableDashboardProfessional', href: '/detailedDashboard' },
            { title: 'ترکیبی', icon: '', access: 'bEnableDashboardHybrid', href: '/hybridDashboard' },
            { title: 'شیلات', icon: '', access: 'bFisheries_Dashboard', href: '/fisheries-dashbord' },
            {
              title: 'پیش ثبت‌نام‌ها',
              icon: '',
              access: 'bFleetYar_Advance_PreregistratioDashboard',
              href: 'vehicle-management/pre-registration/dashboard',
            },
            { title: 'ایمنی', icon: '', access: 'bSafetyIndexDashboard', href: 'safety-dashboard' },
            { title: 'وضعیت ناوگان', icon: '', href: 'fleet-status' },
          ],
        },
      ],
    },
    {
      title: 'وقایع و ورودی‌ها',
      icon: 'fa-bolt',
      children: [
        { title: 'مدیریت وقایع', icon: 'fa-cubes', access: 'bLiveJournal', href: '/manageEvent' },
        { title: 'وقایع روزانه', icon: 'fa-cubes', access: 'bLiveJournal', href: '/daily-event' },
        { title: 'SOS مدیریت', icon: 'fa-exclamation-triangle', access: 'bLiveJournal', href: '/manageSos' },
        { title: 'مدیریت ورودی ها', icon: 'fa-briefcase', access: 'bIOManage', href: '/manageInput' },
        { title: 'نقشه وضعیت خدمات رسانی', icon: 'fa-copy', access: 'bServiceDelivery', route: '/mapInput/report', newTab: true },
      ],
    },
    {
      title: 'مدیریت وسایل نقلیه',
      icon: 'fa-car',
      access: ['bDonotShowMap', 'bEnableAddID', 'bFleetYar_Advance_PreregistrationManagment', 'bFleetYar_Advance_TankerPreregistrationManagment', 'bFleetYar_Installer', 'bEnableAddCar', 'bCarAdded', 'bAbortion', 'bServiceParts_Manage', 'bAuthentication'],
      children: [
        { title: 'موقعیت خودروها در زمان', icon: 'fa-map-marker', access: 'bDonotShowMap', href: '/vehicle-management/location-timeline' },
        { title: 'فهرست خودرو ها', icon: 'fa-list', route: '/carList' },
        { title: 'ثبت ردیاب', icon: 'fa-usb', access: 'bEnableAddID', route: '/device' },
        { title: 'تکمیل شناسنامه ردیاب', icon: 'fa-usb', access: 'bEnableAddID', route: '/device/registration' },
        {
          title: 'پیش ثبت‌نام شیلات',
          icon: 'fa-file-text-o',
          access: 'bFleetYar_Advance_PreregistrationManagment',
          href: '/vehicle-management/pre-registration/shilat',
        },
        {
          title: 'پیش ثبت‌نام تانکر',
          icon: 'fa-file-text-o',
          access: 'bFleetYar_Advance_TankerPreregistrationManagment',
          href: '/vehicle-management/pre-registration/tanker',
        },
        { title: 'کارشناس نصب', icon: 'fa-user-circle', access: 'bFleetYar_Installer', href: '/vehicle-management/installer' },
        { title: 'آمار و تجهیزات', icon: 'fa-info-circle', access: 'bEquipmentStatistics', href: '/vehicle-management/division' },
        { title: 'ثبت خودرو جدید', icon: 'fa-car', access: 'bEnableAddCar', action: () => a.openAddVehicle() },
        { title: 'خودروهای اضافه شده', icon: 'fa-car', access: 'bCarAdded', href: '/vehicle-management/added-cars' },
        { title: 'خودروهای اسقاطی', icon: 'fa-car', access: 'bAbortion', href: '/vehicle-management/scrap-cars' },
        { title: 'درخواست‌های خدمات و قطعه', icon: 'fa-wrench', access: 'bServiceParts_Manage', route: '/service-parts', newTab: true },
        { title: 'احراز هویت', icon: 'fa-id-badge', access: 'bAuthentication', route: '/validation', newTab: true },
      ],
    },
    {
      title: 'اتوماسیون سفر',
      icon: 'fa-plane',
      children: [
        {
          title: 'سفر های اداری',
          access: 'OffitialTrip_Trip',
          children: [
            { title: 'ثبت سفر', icon: 'fa-plane', access: 'OffitialTrip_SaveTrip', action: () => a.openTripModal() },
            { title: 'سفر های پرسنل من', icon: 'fa-plane', access: 'OffitialTrip_MyPersonelTrip', href: '/trip/automation/staff-travel' },
            { title: 'مدیریت سفرها', icon: 'fa-plane', access: 'OffitialTrip_ManageTrips', href: '/trip/automation/manage-travel' },
            { title: 'برنامه ریزی سفرهای پرسنل', icon: 'fa-plane', access: 'OffitialTrip_TripsPanning', href: '/trip/automation/planning-travel' },
          ],
        },
        {
          title: 'جستجو و داشبورد',
          access: 'OffitialTrip_Search',
          children: [
            { title: 'جستجوی سفرهای اداری', icon: 'fa-area-chart', access: 'OffitialTrip_TripsSearch', href: 'trip/dashboard/search-trips' },
            {
              title: 'بیوریتم رانندگان',
              icon: 'fa-area-chart',
              access: 'OffitialTrip_DriverBioritm',
              route: '/trip/dashboard/search-driver',
              newTab: true,
            },
            {
              title: 'داشبورد سفرهای اداری',
              icon: 'fa-area-chart',
              access: 'OffitialTrip_Dashboard',
              route: '/trip/dashboard/office-travel',
              newTab: true,
            },
            {
              title: 'نمودار کیفی رانندگان',
              icon: 'fa-area-chart',
              access: 'OffitialTrip_DriverQualityReport',
              route: '/trip/dashboard/quality-chart-drivers',
              newTab: true,
            },
            { title: 'وضعیت سفرها', icon: 'fa-area-chart', access: 'OffitialTrip_TripStatus', route: '/trip/dashboard/trip-status', newTab: true },
          ],
        },
        {
          title: 'گزارش های عملکردی',
          access: 'OffitialTrip_FunctionReport',
          children: [
            {
              title: 'کارکرد رانندگان به تفکیک تاریخ',
              icon: 'fa-list',
              access: 'OffitialTrip_DriverFuncationReport',
              href: 'trip/report/drivers-function',
            },
            {
              title: 'سرجمع کارکرد رانندگان',
              icon: 'fa-list',
              access: 'OffitialTrip_SumDriverFuncationReport',
              href: 'trip/report/drivers-total-function',
            },
            {
              title: 'تعداد سفر به تفکیک جایگاه',
              icon: 'fa-list',
              access: 'OffitialTrip_TripCountBaseChartReport',
              href: '/reportage/TripBaseChart',
            },
            { title: 'سفرهای برنامه ریزی شده', icon: 'fa-list', access: 'OffitialTrip_TripPlanningReport', href: 'trip/report/planning-trip' },
            {
              title: 'حضور و غیاب رانندگان',
              icon: 'fa-list',
              access: 'OffitialTrip_DriversPresenceReport',
              href: '/trip/report/presence-absence-drivers',
            },
            { title: 'گزارش سفر به تفکیک جایگاه', icon: 'fa-list' },
          ],
        },
        {
          title: 'گزارش های مالی',
          access: 'OffitialTrip_FinanceReport',
          children: [
            {
              title: 'بررسی و تایید کارکرد رانندگان',
              icon: 'fa-list',
              access: 'OffitialTrip_FinanceDriversFunctionReport',
              href: '/reportage/financial-reports',
            },
            { title: 'صدور فیش حقوق رانندگان', icon: 'fa-list' },
            { title: 'کسورات قانونی و فیش حقوق', icon: 'fa-list', access: 'OffitialTrip_FinanceDriversPayslipReport' },
          ],
        },
        {
          title: 'چارت،پرسنل،کاربران اداری',
          access: 'OffitialTrip_UserManagment',
          children: [
            { title: 'کاربران اتوماسیون اداری', icon: 'fa-list', access: 'OffitialTrip_UserManagment_User_Save', href: 'trip/manage-users/new-user' },
            { title: 'پرسنل اداری', icon: 'fa-list', access: 'OffitialTrip_UserManagment_Passenger_Save', href: 'trip/dashboard/search-personnel' },
            { title: 'چارت سازمانی', icon: 'fa-list', access: 'OffitialTrip_UserManagment_Chart', href: 'trip/manage-users/chart-definition' },
            {
              title: 'دسترسی چارت سازمانی',
              icon: 'fa-list',
              access: 'OffitialTrip_UserManagment_Chart_Ability',
              href: 'trip/manage-users/chart-access-determine',
            },
          ],
        },
        {
          title: 'اطلاعات پایه',
          access: 'OffitialTrip_BaseInfo',
          children: [
            { title: 'اطلاعات پایه اتوماسیون', icon: 'fa-list', access: 'OffitialTrip_BaseInfo_Save', route: '/tripAutomation' },
            { title: 'حوزه خدمت', icon: 'fa-list', access: 'OffitialTrip_BaseInfo_MobileDomain', route: '/tripAutomation/serviceArea' },
            { title: 'ثبت و ویرایش تعطیلات', icon: 'fa-calendar', access: 'OffitialTrip_BaseInfo_Holiday', action: () => a.openHolidayModal() },
            { title: 'تعرفه خدمات', icon: 'fa-list', access: 'OffitialTrip_BaseInfo_Tariff', route: 'basic-information/service-costs' },
            { title: 'مسیرهای پرتردد', icon: 'fa-road', access: 'OffitialTrip_BaseInfo_SAD', href: '/basic-information/busy-routes' },
            { title: 'اخبار', icon: 'fa-list', access: 'OffitialTrip_BaseInfo_News', href: 'basic-information/news' },
          ],
        },
      ],
    },
    {
      title: 'مدیریت کاربران',
      icon: 'fa-users',
      children: [
        { title: 'تغییر کلمه عبور', icon: 'fa-key', access: 'bChangePass', route: '/changePass' },
        { title: 'کاربران زیر مجموعه شما', icon: 'fa-user-circle', access: 'bEnableIntroducedMobile', route: '/user' },
        { title: 'مدیریت کاربران', icon: 'fa-users', access: 'bUserManagment', route: '/saveUser' },
        { title: 'کاربران برخط', icon: 'fa-circle text-success', access: 'bUserManagment_OnlineUser', route: '/online-users' },
        { title: 'گروه کاربری و دسترسی‌ها', icon: 'fa-universal-access', access: 'bUserManagment_Group', href: '/roles-roleAccesses' },
        { title: 'مدیریت تیکت ها', icon: 'fa-envelope', access: 'bSupport', href: '/supportManager' },
        { title: 'حوزه های خدمتی', icon: 'fa-university', access: 'bMobileDomain_Save', href: '/ServiceAreas' },
      ],
    },
    {
      title: 'حمل و نقل هوشمند',
      icon: 'fa-bus',
      children: [
        {
          title: 'ترانزیت',
          access: ['bTransit', 'bTransit_Trip', 'bTransit_Trip_Report'],
          children: [
            { title: 'مدیریت مسیر ها', icon: 'fa-road', route: '/transit/route-management', newTab: true },
            { title: 'ثبت بارنامه', icon: 'fa-info-circle', access: 'bTransit_Trip', route: '/transit/barnameh', newTab: true },
            { title: 'گزارش عملکرد', icon: 'fa-info-circle', access: 'bTransit_Trip_Report', route: '/transit/performance-report', newTab: true },
            {
              title: 'گزارش آزاد عملکرد',
              icon: 'fa-info-circle',
              access: 'bTransit_Trip_Report',
              route: '/transit/without-planning-report',
              newTab: true,
            },
            { title: 'گزارش ترانزیت تایم', icon: 'fa-info-circle', access: 'bTransit_Trip_Report', route: '/transit/time-report', newTab: true },
            { title: 'داشبورد ترانزیت', icon: 'fa-info-circle' },
          ],
        },
        {
          title: 'ایاب و ذهاب گروهی',
          access: ['bRoute', 'bRoute_Route_Schdeule', 'bRoute_Management', 'bTransportation_Performance', 'bRoute_Reports'],
          children: [
            { title: 'مدیریت مسیر', icon: 'fa-road', access: 'bRoute_Management', href: '/route-management' },
            { title: 'برنامه سرویس', icon: 'fa-calendar', access: 'bRoute_Route_Schdeule', href: '/route-schedule' },
            { title: 'تحلیل عملکرد ایاب و ذهاب', icon: 'fa-car', access: 'bRoute_Reports', href: '/report/0000000/21' },
            { title: 'تحلیل گزارش عملکرد مسیر ها', icon: 'fa-road', access: 'bTransportation_Performance', href: '/report/0000000/21-1' },
            { title: 'سرجمع عملکرد خودرو براساس مسیر', icon: 'fa-car', access: 'bRoute_Reports', href: '/report/0000000/22' },
            { title: 'سرجمع عملکرد راننده براساس مسیر', icon: 'fa-road', access: 'bRoute_Reports', href: '/report/0000000/23' },
            { title: 'گزارش جزئیات پرسنل در سرویس', icon: 'fa-bar-chart', href: 'personnel/detail-service' },
            { title: 'داشبورد ایاب ذهاب', icon: 'fa-bar-chart', access: 'bRoute_Reports', href: '/route-dashboard' },
            { title: 'داشبورد وضعیت سرویس ها', icon: 'fa-bar-chart', access: 'bRoute_Service_Dashboard', href: '/ayab-zahab/dashboard' },
          ],
        },
        {
          title: 'تاکسی لون',
          access: ['bTaxiLon', 'bTaxiLon_ManageTrip', 'bTaxiLon_Drivers', 'bTaxiLon_Reports', 'bTaxiLon_PaymentReport'],
          children: [
            { title: 'مدیریت تاکسی بین شهری', icon: 'fa-list-ul', access: 'bTaxiLon_ManageTrip', href: '/taxiLon/manage-requests' },
            { title: 'مدیریت تاکسی شهری', icon: 'fa-list-ul', access: 'bTaxiLon_City_ManageTrip', href: '/taxiLon/manage-requests-city' },
            { title: 'مدیریت رانندگان', icon: 'fa-id-card', access: 'bTaxiLon_Drivers', href: '/taxiLon/manage-drivers' },
            { title: 'مدیریت آگهی‌ها', icon: 'fa-newspaper-o', access: 'bTaxiLon_Advertisement', href: '/taxiLon/manage-ads' },
            { title: 'رزرو تاکسی بین شهری', icon: 'fa-car', access: 'bTaxiLon_Trip_Save', action: () => a.openTaxiReservation(false) },
            { title: 'رزرو تاکسی شهری', icon: 'fa-car', access: 'bTaxiLon_Trip_Save', action: () => a.openTaxiReservation(true) },
            { title: 'جستجوی سفر‌ها', icon: 'fa-search', access: 'bTaxiLon_Trip_Search', href: '/taxiLon/search-trip' },
            { title: 'جستجوی مسافران', icon: 'fa-search', access: 'bTaxiLon_User_Search', href: '/taxiLon/search-passenger' },
            { title: 'گزارش پرداخت ها', icon: 'fa-info-circle', access: 'bTaxiLon_PaymentReport', href: '/taxiLon/report-payment' },
            { title: 'گزارش‌ها و داشبورد', icon: 'fa-info-circle', access: 'bTaxiLon_Reports', href: '/taxiLon/dashbord-reports' },
            { title: 'بدهکاری/بستانکاری رانندگان', icon: 'fa-info-circle', access: 'bTaxiLon_DriverDebitCredit', href: '/taxiLon/debit-credit' },
            { title: 'برنامه سفرهای رانندگان', icon: 'fa-calendar', access: 'bTaxiLon_DriverCalendar', href: '/taxiLon/driver-calendar' },
            { title: 'تعرفه‌ها', icon: 'fa-money', access: 'bTaxiLon_Tariff', href: '/taxiLon/tariff' },
            { title: 'پشتیبانی و شکایات', icon: 'fa-headphones', access: 'bTaxiLon_TiketManagment', href: '/taxiLon/support' },
          ],
        },
        {
          title: 'امدادران',
          access: ['bEmdadRan', 'bEmdadRan_TiketManagment', 'bEmdadRan_Drivers', 'bEmdadRan_User_Search', 'bEmdadRan_ManageTrip', 'bEmdadRan_Resume'],
          children: [
            { title: 'مدیریت درخواست ها', icon: 'fa-list-ul', access: 'bEmdadRan_ManageTrip', href: '/emdadran/manage-requests' },
            { title: 'مدیریت آگهی‌ها', icon: 'fa-newspaper-o', access: 'bTaxiLon_Advertisement', href: '/taxiLon/manage-ads' },
            { title: 'مدیریت کسب و کار‌ها', icon: 'fa-briefcase', access: 'bEmdadRan_Resume', href: '/emdadran/manage-business' },
            { title: 'مدیریت رانندگان', icon: 'fa-id-card', access: 'bEmdadRan_Drivers', href: '/taxiLon/manage-drivers' },
            { title: 'متقاضیان', icon: 'fa-search', access: 'bEmdadRan_User_Search', href: '/taxiLon/search-passenger' },
            { title: 'جستجوی درخواست‌ها', icon: 'fa-search', href: '/emdadran/search-requests' },
            { title: 'تعرفه‌ها', icon: 'fa-money', access: 'bEmdadRan_Tariff', href: '/taxiLon/tariff-2' },
            { title: 'گزارش پرداخت ها', icon: 'fa-info-circle', access: 'bFleetYar_PaymentReport', href: '/emdadran/report-payment' },
            { title: 'گزارش‌ها و داشبورد', icon: 'fa-info-circle', access: 'bEmdadRan_Dashboard', href: '/emdadran/dashbord-reports' },
            { title: 'بدهکاری/بستانکاری رانندگان', icon: 'fa-info-circle', access: 'bEmdadRan_DriverDebitCredit', href: '/emdadran/debit-credit' },
            { title: 'پشتیبانی و شکایات', icon: 'fa-headphones', access: 'bEmdadRan_TiketManagment', href: '/taxiLon/support' },
          ],
        },
        {
          title: 'اتوبوس شهری',
          children: [
            { title: 'مدیریت خطوط اتوبوسرانی', icon: 'fa-bus', access: 'bBusEdit', href: '/busLine' },
            {
              title: 'گزارش حضور اتوبوس در ایستگاه ها',
              icon: 'fa-slideshare',
              access: 'bBusPresenceAtStations',
              route: ['/report', 'busPresence'],
              newTab: true,
            },
            {
              title: 'گزارش حضور اتوبوس ها در ایستگاه',
              icon: 'fa-bus',
              access: 'bBusesPresenceAtStation',
              route: ['/report', 'station'],
              newTab: true,
            },
          ],
        },
        {
          title: 'تاکسی شهری',
          access: ['bTaxi_LineManagment', 'bTaxiADSReport', 'bTaxiSaveADS', 'bTaxiDashboard', 'bSaveTaxiMeter'],
          children: [
            { title: 'مدیریت خطوط تاکسی', icon: 'fa-road', access: 'bTaxi_LineManagment', href: '/taxi-line' },
            { title: 'ثبت تعرفه تاکسی', icon: 'fa-taxi', access: 'bSaveTaxiMeter', route: '/taxiMeter' },
            { title: 'داشبورد تاکسی', icon: 'fa-taxi', access: 'bTaxiDashboard', href: '/dashbordTaxi' },
            { title: 'ثبت تبلیغات', icon: 'fa-taxi', access: 'bTaxiSaveADS', route: '/advertiseTaxi' },
            { title: 'داشبورد تبلیغات', icon: 'fa-taxi', access: 'bTaxiADSReport', href: '/dashbordAdds' },
            { title: 'مدیریت مرخصی', icon: 'fa-tasks', access: 'bTaxi_Vacation_Managment', href: '/vehicle-management/taxi/manage-vacation' },
            { title: 'گزارش عملکرد ماهیانه', icon: 'fa-tasks', access: 'bTaxi_OnlineDashboard', href: '/vehicle-management/taxi/monthly-report' },
            {
              title: 'داشبورد وضعیت فعلی ناوگان',
              icon: 'fa-tasks',
              access: 'bTaxi_OnlineDashboard',
              href: '/vehicle-management/taxi/status-dashboard',
            },
            { title: 'داشبورد جامع', icon: 'fa-tasks', access: 'bTaxi_OnlineDashboard', href: '/vehicle-management/taxi/dashboard' },
          ],
        },
      ],
    },
    {
      title: 'IOT',
      icon: 'fa-microchip',
      access: 'bIOT',
      children: [
        {
          title: 'LoadWatch',
          access: 'bLoadWatch',
          children: [
            { title: 'لیست دیزل‌ها', icon: 'fa-list', access: 'bLoadWatch_List', route: '/iot/load-watch/list', newTab: true },
            { title: 'داشبورد برخط', icon: 'fa-list', access: 'bLoadWatch_Dashboard', route: '/iot/load-watch/dashboard', newTab: true },
            { title: 'گزارش عملکرد', icon: 'fa-list', route: '/iot/load-watch/performance', newTab: true },
            { title: 'وقایع دیزل', icon: 'fa-list' },
          ],
        },
      ],
    },
    {
      title: 'لوجستیک',
      icon: 'fa-truck',
      children: [
        {
          title: 'مدیریت تانکرها',
          access: ['bTanker_Dashboard', 'bTanker_Shower_Edit', 'bTanker_List', 'bTanker_Reports'],
          children: [
            {
              title: 'گزارش ها',
              access: 'bTanker_Reports',
              children: [
                { title: 'تحلیل تخلیه ها', icon: 'fa-file-text-o', href: '/tanker/analysis-discharges' },
                { title: 'گزارش وضعیت تانکر ها', icon: 'fa-file-text-o', href: '/tanker/Report' },
                { title: 'شرح تامین به تفکیک منبع', icon: 'fa-file-text-o', href: '/tanker/base-Source' },
                { title: 'شرح تامین به تفکیک تانکر', icon: 'fa-file-text-o', href: '/tanker/base-Tanker' },
                { title: 'درصد آبگیری‌های غیر‌مجاز', icon: 'fa-file-text-o', href: '/tanker/illegal-water-percentage' },
                { title: 'رتبه‌بندی رانندگان پرخطر', icon: 'fa-file-text-o', href: '/tanker/drivers-risk-ranking' },
                { title: 'نقشه نقاط آبگیری غیر‌مجاز', icon: 'fa-file-text-o', href: '/tanker/illegal-water-sources' },
              ],
            },
            { title: 'مدیریت مشتریان', icon: 'fa-address-book', access: 'bTanker_Tanker_WaterClient', href: '/tanker/customer-management' },
            { title: 'امور', icon: 'fa-map-o', href: '/tanker/areas' },
            { title: 'گزارش اجرا', icon: 'fa-file-text-o', href: '/tanker/execution-report' },
            { title: 'درخواست‌های آب', icon: 'fa-file-text-o', href: '/tanker/water-requests' },
            { title: 'شناسنامه تانکر', icon: 'fa-truck', access: 'bTanker_List', href: '/tanker/list' },
            { title: 'محل های تامین', icon: 'fa-database', access: 'bTanker_Shower_Edit', href: '/tanker/Supply-locations' },
            { title: 'داشبورد', icon: 'fa-bar-chart', access: 'bTanker_Dashboard', href: '/tanker/dashbord' },
          ],
        },
        {
          title: 'اجاره وسیله نقلیه',
          access: 'bRent',
          children: [
            {
              title: 'گزارش و داشبورد',
              access: 'bRent_Report',
              children: [
                { title: 'عملکرد به تفکیک تاریخ', icon: 'fa-file-text-o', href: '/vehicle-rental/performance-byDate' },
                { title: 'جزئیات اجاره', icon: 'fa-file-text-o', href: '/vehicle-rental/rental-details' },
                { title: 'داشبورد', icon: 'fa-dashboard', href: '/vehicle-rental/rent-dashboard' },
              ],
            },
            { title: 'اجاره‌های فعال', icon: 'fa-car', access: 'bRent_Active', href: '/vehicle-rental/active' },
            { title: 'اطلاعات پایه', icon: 'fa-info-circle', access: 'bRent_BaseInfo', href: '/vehicle-rental/base-information' },
          ],
        },
        {
          title: 'تلمتری CAN',
          access: 'bTelemetry',
          children: [
            { title: 'داشبورد وضعیت شارژ', icon: 'fa-battery', access: 'bTelemetry_ChargeStatus', route: '/telemetry/charge-status', newTab: true },
            { title: 'تحلیل اطلاعات CAN', icon: 'fa-bar-chart', access: 'bTelemetry_ChargeStatus', route: '/telemetry/can-analyzer', newTab: true },
            { title: 'داشبورد جامع', icon: 'fa-bar-chart', route: '/telemetry/dashboard', newTab: true },
            { title: 'داشبورد ایمنی', icon: 'fa-bar-chart', route: '/telemetry/safety-dashboard', newTab: true },
          ],
        },
        {
          title: 'فیلیت دیاگ',
          access: 'bFleetDiag',
          children: [
            { title: 'کاربران', icon: 'fa-users', route: '/fleetdiag/users', newTab: true },
          ],
        },
      ],
    },
    {
      title: 'نظارت تصویری',
      icon: 'fa-video-camera',
      access: ['bVideoSurveillance', 'bVideoSurveillance_Parking', 'bVideoSurveillance_LastUpload', 'bVideoSurveillance_Dashboard', 'bVideoSurveillance_Events'],
      children: [
        { title: 'دوربین‌ها', icon: 'fa-camera', href: '/cctv-monitoring/cameras' },
        { title: 'پارکینگ‌‌ها', icon: 'fa-camera', access: 'bVideoSurveillance_Parking', href: '/cctv-monitoring/video-locations' },
        { title: 'مدیریت فیلم‌ها', icon: 'fa-camera', access: 'bVideoSurveillance_LastUpload', href: '/cctv-monitoring/film-report' },
        { title: 'وضعیت تخلیه فیلم‌ها', icon: 'fa-camera', href: '/cctv-monitoring/status' },
        { title: 'وقایع رخ داده', icon: 'fa-hashtag', access: 'bVideoSurveillance_Events' },
        { title: 'نقشه وقایع', icon: 'fa-map', access: 'bVideoSurveillance_Events' },
      ],
    },
    {
      title: 'مدونیک',
      icon: 'fa-graduation-cap',
      children: [
        {
          title: 'اطلاعات پایه',
          children: [
            { title: 'ثبت مدرسه', icon: 'fa-graduation-cap', access: 'bSchoolService_MobileDomain', route: '/school/newSchool' },
            { title: 'تعیین مدارس کاربر', icon: 'fa-university', access: 'bSchoolService_UserMobileDomain', route: '/school/userSchool' },
            { title: 'ثبت دانش آموز', icon: 'fa-user-plus', access: 'bSchoolService_Student_Save', route: '/school/newStudent' },
            { title: 'کلاس های فوق برنامه', icon: 'fa-university', access: 'bSchool_Manager_SuperClass', href: 'school/extra-classes' },
            { title: 'اخبار', icon: 'fa-list', access: 'bSchool_News', href: 'basic-information/news' },
          ],
        },
        {
          title: 'جستجو و مرور',
          access: ['bSchool_School_Financial_Report', 'bSchool_PM_ManageSchool_VoiceMessage', 'bSchool_PM_ManageSchool_Events', 'bSchoolService_ChangeAddress', 'bSchool_AttendingExams', 'bSchoolService_SchoolAbsence', 'bSchoolService_Absence', 'bSchoolService_Absences', 'bSchoolService_Student_Search'],
          children: [
            { title: 'جستجوی دانش آموز', icon: 'fa-search', access: 'bSchoolService_Student_Search', href: '/school/searchStudent' },
            { title: 'غیبت به مدرسه', icon: 'fa-user-times', access: 'bSchoolService_SchoolAbsence', href: '/school/absenceTo-School' },
            { title: 'گزارش ورود و خروج', icon: 'fa-user-times', access: 'Madonik_EntryExit', href: '/school/entry-exit' },
            { title: 'همایش ها و رخداد ها', icon: 'fa-calendar-check-o', access: 'bSchool_PM_ManageSchool_Events', href: '/school/conferences' },
            { title: 'پیام مدیر', icon: 'fa-comments-o', access: 'bSchool_PM_ManageSchool_VoiceMessage', href: '/school/manager-Message' },
            { title: 'گزارش های روزانه', icon: 'fa-info-circle', access: 'bSchoolService_DailyReports', href: '/school/daily-report' },
          ],
        },
        {
          title: 'گزارش و داشبورد',
          access: ['bSchool_School_Dashboard', 'bSchoolService_Dashboard', 'bSchoolService_DashboardLive', 'bSchoolService_DriverFunction', 'bSchoolService_MonitoringDashboard'],
          children: [
            { title: 'دانش آموزان حاضر', icon: 'fa-hand-paper-o', href: '/school/present-Students' },
            { title: 'دانش آموزان حاضر در امتحان', icon: 'fa-hand-paper-o', access: 'bSchool_AttendingExams', href: '/school/AttendingExams' },
            { title: 'دانش آموزان غایب', icon: 'fa-user-times', access: 'bSchoolService_Absences', href: '/school/absence-Students' },
            { title: 'داشبورد تجمیعی', icon: 'fa-bar-chart', access: 'bSchoolService_Dashboard', href: '/school/accumulative-Dashboard' },
            { title: 'داشبورد مدرسه', icon: 'fa-bar-chart', access: 'bSchool_School_Dashboard', href: '/school/school-Dashboard' },
            { title: 'داشبورد نظارتی', icon: 'fa-bar-chart', access: 'bSchoolService_MonitoringDashboard', href: '/school/monitoring-dashboard' },
          ],
        },
        {
          title: 'شکایات و تشکر ها',
          access: ['bSchoolService_Tiket4Rayan', 'bSchoolService_Tiket4School', 'bSchoolService_Tiket'],
          children: [
            { title: 'پیشنهادات و راه کارها', icon: 'fa-commenting', access: 'bSchoolService_Tiket4School', href: '/school/solutions' },
            { title: 'پشتیبانی نرم افزار', icon: 'fa-commenting', access: 'bSchoolService_Tiket4Rayan', href: '/school/school-Support' },
          ],
        },
        {
          title: 'بوفه',
          children: [
            { title: 'ثبت کالای بوفه', icon: 'fa-coffee', access: 'bSchool_BuffetSave', href: '/school/buffet' },
            { title: 'پیش فروش های بوفه', icon: 'fa-coffee', access: 'bSchool_PrebuyReport', href: '/school/presell-Buffet' },
            { title: 'گزارش فروش بوفه', icon: 'fa-coffee', access: 'bSchool_BuffetReport', href: '/school/buffetSales-Report' },
          ],
        },
        {
          title: 'صدای دانش آموز',
          access: ['bSchoolVoice', 'bSchoolVoice_Request_Save', 'bSchoolVoice_CartableDefine'],
          children: [
            { title: 'تعریف کارتابل پرسنل', icon: 'fa-user', access: 'bSchoolVoice_CartableDefine', href: 'school/request-allocation' },
            { title: 'درخواست والدین', icon: 'fa-user', access: 'bSchoolVoice_Request_Save', href: 'school/parent-requests' },
            { title: 'رسیدگی به درخواست', icon: 'fa-search', access: 'bSchool_Request_Cartable' },
            { title: 'جستجو', icon: 'fa-search', access: 'bSchoolVoice_Request_Search', href: 'school/search-requests' },
            { title: 'داشبورد', icon: 'fa-pie-chart', access: 'bSchoolVoice_Request_Dashboard' },
          ],
        },
        {
          title: 'سرویس مدرسه',
          access: 'bSchoolService',
          children: [
            {
              title: 'تعاریف پایه',
              children: [
                {
                  title: 'مدیریت پیمانکاران',
                  icon: 'fa-graduation-cap',
                  access: ['bSchool_Contractors', 'bSchool_ContractorInfo'],
                  href: '/school/contractor',
                },
                { title: 'تعرفه سرویس مدرسه', icon: 'fa-usd', access: 'bMadonik_Tariff', href: '/school/service-fee' },
                {
                  title: 'تعیین سرویس دانش آموزان',
                  icon: 'fa-map-o',
                  access: 'bSchoolService_DetermineDriver',
                  href: '/school/service-Determination',
                },
                { title: 'سرویس های راننده', icon: 'fa-male', access: 'bSchoolService_DriverPlan', href: '/school/driverService' },
              ],
            },
            {
              title: 'گزارش و داشبورد',
              children: [
                { title: 'جستجو و مرور راننده', icon: 'fa-file-text-o', href: '/school/searchDrivers' },
                { title: 'متقاضی سرویس مدرسه', icon: 'fa-bus', access: 'bSchoolService_ServiceRequest', href: '/school/serviceApplicant-4school' },
                { title: 'تغییر آدرس', icon: 'fa-map-marker', access: 'bSchoolService_ChangeAddress', href: '/school/change-Address' },
                { title: 'غیبت از سرویس', icon: 'fa-user-times', access: 'bSchoolService_Absence', href: '/school/service-Absence' },
                { title: 'عملکرد رانندگان', icon: 'fa-id-card', access: 'bSchoolService_DriverFunction', href: '/school/driverPerformance' },
                { title: 'داشبورد برخط', icon: 'fa-bar-chart', access: 'bSchoolService_DashboardLive', href: '/school/realTime-Dashboard' },
                { title: 'شکایات از سرویس', icon: 'fa-commenting', access: 'bSchoolService_Tiket', href: '/school/complaints' },
              ],
            },
            {
              title: 'قرارداد و مالی',
              children: [
                { title: 'قراردادها', icon: 'fa-credit-card', access: 'bMadonik_ServiceContract', href: '/school/contracts' },
                { title: 'گزارش مالی', icon: 'fa-credit-card', access: 'bSchool_School_Financial_Report', href: '/school/financial-Report' },
                { title: 'گزارش چک ها', icon: 'fa-credit-card', access: 'bSchool_School_Financial_Report', href: '/school/checks-Report' },
              ],
            },
          ],
        },
      ],
    },
    {
      title: 'راهنما و ارتباط با ما',
      icon: 'fa-life-ring',
      access: ['bAbout', 'bAboutCompany', 'bShop'],
      children: [
        { title: 'درباره برنامه', icon: 'fa-exclamation-circle', access: 'bAbout', action: () => a.openExternalLink(3) },
        { title: 'ارتباط با شرکت', icon: 'fa-building-o', access: 'bAboutCompany', action: () => a.openCompanyInfo() },
        { title: 'راهنما', icon: 'fa-question-circle', action: () => a.openModal('#help-Modal'), hint: 'راهنمای چند رسانه ای سامانه' },
        { title: 'فروشگاه', icon: 'fa-shopping-cart', access: 'bShop', action: () => a.openExternalLink(0) },
        { title: 'اخبار', icon: 'fa-newspaper-o', route: '/news', newTab: true },
        { title: 'آموزشگاه', icon: 'fa-file-video-o', route: '/educations', newTab: true },
        { title: 'مدیریت تبلیغات', icon: 'fa-play-circle', access: 'bAdvertise_Manage', route: '/advertising', newTab: true },
      ],
    },
];
